import { useCallback, useEffect, useMemo, useState } from "react";
import { Centered, Shell } from "./components/Shell";
import { Bilshe } from "./games/Bilshe";
import { Chrono } from "./games/Chrono";
import { Connections } from "./games/Connections";
import { Dezze } from "./games/Dezze";
import { Contexto } from "./games/Contexto";
import { Emovi } from "./games/Emovi";
import { Gerb } from "./games/Gerb";
import { Mat } from "./games/Mat";
import { Mathle } from "./games/Mathle";
import { Persondle } from "./games/Persondle";
import { Arena } from "./games/Arena";
import { Foto } from "./games/Foto";
import { Khaip } from "./games/Khaip";
import { Kviz } from "./games/Kviz";
import { Naoko } from "./games/Naoko";
import { Rebus } from "./games/Rebus";
import { Slova } from "./games/Slova";
import { Slovko } from "./games/Slovko";
import { Slovokolo } from "./games/Slovokolo";
import { Stavky } from "./games/Stavky";
import { Taemne } from "./games/Taemne";
import { Maliunok } from "./games/Maliunok";
import { Dviinyk } from "./games/Dviinyk";
import { Khvylia } from "./games/Khvylia";
import { Vahon } from "./games/Vahon";
import { Zblyzka } from "./games/Zblyzka";
import { Wordle } from "./games/Wordle";
import { Flaggle } from "./games/Flaggle";
import { Redactle } from "./games/Redactle";
import { Travle } from "./games/Travle";
import { Worldle } from "./games/Worldle";
import { Hariache } from "./games/Hariache";
import { Susidy } from "./games/Susidy";
import { Stolytsia } from "./games/Stolytsia";
import { Queens } from "./games/Queens";
import { Stado } from "./games/Stado";
import { Hub } from "./Hub";
import { currentRoute, GAME_META, HUB_GROUPS, type GameId } from "./route";
import { colorScheme, initTelegram, tg } from "./telegram";

function GameScreen({ game, date }: { game: GameId; date?: string | undefined }) {
  if (game === "wordle") return <Wordle date={date} />;
  if (game === "connections") return <Connections date={date} />;
  if (game === "contexto") return <Contexto date={date} />;
  if (game === "worldle") return <Worldle date={date} />;
  if (game === "flaggle") return <Flaggle date={date} />;
  if (game === "redactle") return <Redactle date={date} />;
  if (game === "travle") return <Travle date={date} />;
  if (game === "hariache") return <Hariache date={date} />;
  if (game === "susidy") return <Susidy date={date} />;
  if (game === "stolytsia") return <Stolytsia date={date} />;
  if (game === "queens") return <Queens date={date} />;
  if (game === "stado") return <Stado date={date} />;
  if (game === "mathle") return <Mathle date={date} />;
  if (game === "slovko") return <Slovko date={date} />;
  if (game === "persondle") return <Persondle date={date} />;
  if (game === "emovi") return <Emovi date={date} />;
  if (game === "bilshe") return <Bilshe date={date} />;
  if (game === "khaip") return <Khaip date={date} />;
  if (game === "mat") return <Mat date={date} />;
  if (game === "dezze") return <Dezze date={date} />;
  if (game === "gerb") return <Gerb date={date} />;
  if (game === "zblyzka") return <Zblyzka date={date} />;
  if (game === "vahon") return <Vahon date={date} />;
  if (game === "foto") return <Foto date={date} />;
  if (game === "slova") return <Slova date={date} />;
  if (game === "slovokolo") return <Slovokolo date={date} />;
  if (game === "rebus") return <Rebus date={date} />;
  if (game === "stavky") return <Stavky date={date} />;
  if (game === "kviz") return <Kviz date={date} />;
  if (game === "taemne") return <Taemne date={date} />;
  if (game === "maliunok") return <Maliunok date={date} />;
  if (game === "dviinyk") return <Dviinyk date={date} />;
  if (game === "khvylia") return <Khvylia date={date} />;
  if (game === "naoko") return <Naoko date={date} />;
  if (game === "arena") return <Arena date={date} />;
  return <Chrono date={date} />;
}

interface Active {
  game: GameId;
  /** Archive replay date; undefined = today. */
  date?: string;
}

export function App() {
  const route = useMemo(() => currentRoute(), []);
  const [active, setActive] = useState<Active | null>(route.kind === "game" ? { game: route.game } : null);

  useEffect(() => {
    initTelegram();
    document.documentElement.dataset.scheme = colorScheme();
    const app = tg();
    const onTheme = () => {
      document.documentElement.dataset.scheme = colorScheme();
    };
    app?.onEvent("themeChanged", onTheme);
    return () => app?.offEvent("themeChanged", onTheme);
  }, []);

  // Launched from the hub: Telegram's native back button returns to the hub.
  const back = useCallback(() => setActive(null), []);
  useEffect(() => {
    const bb = tg()?.BackButton;
    if (!bb) return;
    if (route.kind === "hub" && active) {
      bb.onClick(back);
      bb.show();
      return () => {
        bb.offClick(back);
        bb.hide();
      };
    }
    bb.hide();
    return;
  }, [route.kind, active, back]);

  if (active) return <GameScreen key={`${active.game}:${active.date ?? "today"}`} game={active.game} date={active.date} />;
  if (route.kind === "hub") return <Hub onOpen={(game, date) => setActive({ game, ...(date ? { date } : {}) })} />;
  // Bare link without start_param — plain menu (results won't attribute to a group).
  return (
    <Shell>
      <Centered>
        <div className="text-lg font-bold">Ігри</div>
        <div className="text-sm text-tghint">Відкривай через кнопки чіпса в чаті — тоді результат піде в групу.</div>
        <div className="mt-2 space-y-3 text-left text-sm">
          {HUB_GROUPS.map((group) => (
            <div key={group.title}>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-tghint">{group.title}</div>
              <ul className="mt-1 space-y-1">
                {group.games.map((id) => (
                  <li key={id}>
                    {GAME_META[id].emoji} {GAME_META[id].title.toLowerCase()} — {GAME_META[id].blurb}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Centered>
    </Shell>
  );
}
